import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchBlogPosts, fetchBlogCategories } from '../services/api';
import { BlogPost, BlogCategory } from '../types/blog';
import dataEmptyImg from '../assets/images/data_empty.png';
import blogMoreImg from '../assets/images/blog_more.svg';
import blogArrowImg from '../assets/images/blog_arrow.svg';

const BlogPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Map i18n language code to API language code
    const getLanguageCode = (lang: string): string => {
        const langCode = lang.split('-')[0].toLowerCase();
        const supportedLanguages = ['en', 'zh', 'fr', 'es'];
        return supportedLanguages.includes(langCode) ? langCode : 'en';
    };

    // Load categories
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const currentLang = getLanguageCode(i18n.language);
                const fetchedCategories = await fetchBlogCategories(currentLang, 'en');
                setCategories(fetchedCategories);
            } catch (err) {
                console.error('Failed to load categories:', err);
                // Fallback to hardcoded categories
                setCategories([
                    { slug: 'growth-observation', name: t('blog.categories.growth-observation', 'Growth Observation') },
                    { slug: 'emotions-psychology', name: t('blog.categories.emotions-psychology', 'Emotions & Psychology') },
                    { slug: 'interactive-play', name: t('blog.categories.interactive-play', 'Interactive Play') },
                ]);
            }
        };

        loadCategories();
    }, [i18n.language, t]);

    // Load blog posts
    useEffect(() => {
        const loadBlogPosts = async () => {
            try {
                setLoading(true);

                const currentLang = getLanguageCode(i18n.language);

                const response = await fetchBlogPosts({
                    lang: currentLang,
                    fallbackLang: 'en',
                    category: selectedCategory === 'All' ? undefined : selectedCategory.toLowerCase(),
                    pageSize: 20,
                });

                setBlogPosts(response.posts);
            } catch (err) {
                console.error('Failed to load blog posts:', err);
                setBlogPosts([]);
            } finally {
                setLoading(false);
            }
        };

        loadBlogPosts();
    }, [selectedCategory, i18n.language, t]);

    // Format date for display
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Get all category options including "All"
    const allCategories = [
        { slug: 'all', name: t('blog.categories.all', 'All') },
        ...categories
    ];

    return (
        <div className="main-wrapper">
            {/* Hero Banner Section */}
            <div className="section-hero-banar is-blog">
                <div className="padding-global">
                    <div className="container-large">
                        <div className="padding-section-medium">
                            <div className="hero-banner-component">
                                <div className="hero-banner-content-wrapper">
                                    <h2 className="heading-style-h2">{t('blog.pageTitle', 'Blog')}</h2>
                                    <div className="hero-banar-content">
                                        <Link to="/" className="home-link">
                                            {t('navbar.home', 'Home')}
                                        </Link>
                                        <div className="hero-banar-dot dot-color-pink"></div>
                                        <div className="hero-banar-text text-color-pink">
                                            {t('blog.breadcrumb', 'Blog')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Blog Section */}
            <div className="section-blog v2">
                <div className="padding-global">
                    <div className="container-large">
                        <div className="padding-section-large padding-bottom-top-medium">
                            <div className="blog-component">
                                {/* Top Content Wrapper */}
                                <div className="blog-top-content-wrapper">
                                    {/* Heading */}
                                    <div className="heading-wrapper">
                                        <h2 className="heading-style-h2">{t('blog.allNews', 'All Latest News')}</h2>
                                        <div className="line-color-pink" style={{ width: '100%', height: '15px' }}></div>
                                    </div>

                                    {/* Category Filter */}
                                    <div className="blog-catagory-collection-list-wrapper w-dyn-list">
                                        <div role="list" className="blog-catagory-collection-list w-dyn-items">
                                            {allCategories.map(category => (
                                                <div key={category.slug} role="listitem" className="blog-catagory-collection-item w-dyn-item">
                                                    <button
                                                        onClick={() => setSelectedCategory(
                                                            category.slug === 'all' ? 'All' : category.slug
                                                        )}
                                                        className={`blog-catagory-item w-inline-block ${
                                                            (selectedCategory === 'All' && category.slug === 'all') ||
                                                            (selectedCategory.toLowerCase() === category.slug.toLowerCase())
                                                                ? 'w--current' : ''
                                                        }`}
                                                    >
                                                        <div>{category.name}</div>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="padding-bottom padding-large"></div>

                                {loading ? (
                                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                                        <p>{t('blog.loading', 'Loading...')}</p>
                                    </div>
                                ) : blogPosts.length === 0 ? (
                                    /* Empty State */
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '4rem 2rem',
                                    }}>
                                        <img
                                            src={dataEmptyImg}
                                            alt={t('blog.empty', 'Ideas in progress')}
                                            style={{ width: '200px', marginBottom: '1.5rem' }}
                                        />
                                        <p style={{
                                            color: '#999',
                                        }}>
                                            {t('blog.empty', 'Ideas in progress')}
                                        </p>
                                    </div>
                                ) : (
                                    /* Blog Collection List - Grid Layout */
                                    <div className="blog-collection-list-wrapper w-dyn-list">
                                        <div role="list" className="blog-collection-list w-dyn-items">
                                            {blogPosts.map((post) => (
                                                <div
                                                    key={post.id}
                                                    role="listitem"
                                                    className="blog-collection-item w-dyn-item"
                                                >
                                                    <div className="blog-item">
                                                        {/* Blog Image */}
                                                        <img
                                                            src={post.image}
                                                            loading="lazy"
                                                            alt={post.title}
                                                            className="blog-image"
                                                        />

                                                        {/* Blog Details Content */}
                                                        <div className="blog-details-content">
                                                            {/* Top Content - Date and Category */}
                                                            <div className="blog-top-content">
                                                                <div className="text-size-medium text-color-green">
                                                                    {formatDate(post.date)}
                                                                </div>
                                                                <img
                                                                    src={blogMoreImg}
                                                                    loading="lazy"
                                                                    alt=""
                                                                />
                                                                <div className="text-size-medium text-color-green">
                                                                    {post.category_name}
                                                                </div>
                                                            </div>

                                                            <div className="padding-bottom padding-xxsmall"></div>

                                                            {/* Blog Content */}
                                                            <div className="blog-content">
                                                                <h5 className="heading-style-h5">
                                                                    {post.title}
                                                                </h5>

                                                                {/* Read More Link */}
                                                                <Link
                                                                    to={`/blog/${post.slug}`}
                                                                    className="blog-bottom w-inline-block"
                                                                >
                                                                    <div className="text-block">{t('blog.readMore', 'Read More')}</div>
                                                                    <img
                                                                        src={blogArrowImg}
                                                                        loading="lazy"
                                                                        alt=""
                                                                        className="blog-arrow"
                                                                    />
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogPage;
